import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from './config/apiConfig';
import './WelcomeExploreUnknownLeaderboard.css';
import './WelcomeLeftStack.css';

const POLL_MS = 30000;

const RANKING_I18N_KEY = Object.freeze({
  'asset-strategies': 'honorHall.assetStrategies',
  'battle-strategies': 'honorHall.battleStrategies',
  'battle-legends': 'honorHall.battleLegends',
  'explorers': 'honorHall.explorers',
  'war-heroes': 'honorHall.warHeroes',
});

/**
 * @param {object} movement
 * @param {(key: string, opts?: object) => string} t
 */
function movementLabel(movement, t) {
  const rankingName = t(RANKING_I18N_KEY[movement.rankingKey] || movement.rankingKey);

  if (movement.entityType === 'strategy') {
    if (!movement.strategyName) {
      throw new Error('Welcome honor movements row missing strategyName');
    }
    if (movement.movementType === 'entered') {
      return t('welcome.honorHallMovements.enteredStrategy', {
        strategy: movement.strategyName,
        ranking: rankingName,
      });
    }
    return t('welcome.honorHallMovements.leftStrategy', {
      strategy: movement.strategyName,
      ranking: rankingName,
    });
  }

  if (!movement.username) {
    throw new Error('Welcome honor movements row missing username');
  }
  if (movement.movementType === 'entered') {
    return t('welcome.honorHallMovements.enteredPlayer', {
      username: movement.username,
      ranking: rankingName,
    });
  }
  return t('welcome.honorHallMovements.leftPlayer', {
    username: movement.username,
    ranking: rankingName,
  });
}

/**
 * @param {{ isMobile?: boolean, isDevPlayer1?: boolean }} props
 */
function WelcomeHonorHallMovements({ isMobile = false, isDevPlayer1 = false }) {
  const { t } = useTranslation();
  const [movements, setMovements] = useState([]);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mockLoading, setMockLoading] = useState(false);
  const [mockError, setMockError] = useState(null);

  const loadMovements = useCallback(async () => {
    const response = await fetch(`${API_URL}/honor-hall/welcome-movements`);
    if (!response.ok) {
      const errText = await response.text().catch(() => response.statusText);
      throw new Error(`welcome-movements HTTP ${response.status}: ${errText}`);
    }
    const data = await response.json();
    if (!Array.isArray(data.movements)) {
      throw new Error('welcome-movements response missing movements array');
    }
    return data.movements;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const pollMovements = async () => {
      try {
        const nextMovements = await loadMovements();
        if (!cancelled) {
          setMovements(nextMovements);
          setFetchFailed(false);
          setLoading(false);
        }
      } catch (err) {
        console.error('Welcome: honor-hall welcome-movements fetch failed:', err);
        if (!cancelled) {
          setFetchFailed(true);
          setLoading(false);
        }
      }
    };

    pollMovements();
    const id = setInterval(pollMovements, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [loadMovements]);

  const handleDevMockMovements = async () => {
    setMockLoading(true);
    setMockError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Missing auth token');
      }
      const response = await fetch(`${API_URL}/honor-hall/dev-mock-welcome-movements`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${response.status}`);
      }
      const data = await response.json();
      if (!Array.isArray(data.movements)) {
        throw new Error('dev-mock response missing movements array');
      }
      setMovements(data.movements);
      setFetchFailed(false);
      setLoading(false);
    } catch (err) {
      console.error('Welcome: dev mock honor movements failed:', err);
      setMockError(err.message || 'Dev mock failed');
    } finally {
      setMockLoading(false);
    }
  };

  return (
    <aside
      className={`welcome-eu-leaderboard welcome-honor-movements${isMobile ? ' welcome-eu-leaderboard--mobile' : ''}`}
      aria-label={t('welcome.honorHallMovements.ariaLabel')}
    >
      <div className="welcome-eu-leaderboard__safe-area">
      <span className="welcome-eu-leaderboard-corner welcome-eu-leaderboard-corner--tr" aria-hidden="true" />
      <span className="welcome-eu-leaderboard-corner welcome-eu-leaderboard-corner--br" aria-hidden="true" />
      <div className="welcome-eu-leaderboard-header">
        <img
          src="/images/honorhall.png"
          alt=""
          className="welcome-eu-leaderboard-icon"
          aria-hidden="true"
        />
        <h2 className="welcome-eu-leaderboard-title">{t('welcome.honorHallMovements.title')}</h2>
      </div>

      {isDevPlayer1 && (
        <div className="welcome-honor-movements-dev">
          <button
            type="button"
            className="welcome-honor-movements-dev-btn"
            onClick={handleDevMockMovements}
            disabled={mockLoading}
          >
            {mockLoading
              ? t('welcome.honorHallMovements.devMockLoading')
              : t('welcome.honorHallMovements.devMockButton')}
          </button>
          {mockError && (
            <p className="welcome-eu-leaderboard-status welcome-eu-leaderboard-status--error">
              {mockError}
            </p>
          )}
        </div>
      )}

      {loading && movements.length === 0 && !fetchFailed && (
        <p className="welcome-eu-leaderboard-status">{t('welcome.honorHallMovements.loading')}</p>
      )}

      {fetchFailed && movements.length === 0 && (
        <p className="welcome-eu-leaderboard-status welcome-eu-leaderboard-status--error">
          {t('welcome.honorHallMovements.loadFailed')}
        </p>
      )}

      {!fetchFailed && !loading && movements.length === 0 && (
        <p className="welcome-eu-leaderboard-status">{t('welcome.honorHallMovements.empty')}</p>
      )}

      {movements.length > 0 && (
        <ol className="welcome-eu-leaderboard-list welcome-eu-leaderboard-list--scroll">
          {movements.map((movement) => (
            <li
              key={`${movement.rankingKey}-${movement.movementType}-${movement.movedAt}`}
              className="welcome-eu-leaderboard-entry"
            >
              <span className="welcome-eu-leaderboard-rank" aria-hidden="true">
                {t(`welcome.honorHallMovements.rankingShort.${movement.rankingKey}`)}
              </span>
              <div className="welcome-eu-leaderboard-body">
                <div
                  className={`welcome-eu-leaderboard-movement welcome-eu-leaderboard-movement--${movement.movementType}`}
                >
                  {movementLabel(movement, t)}
                </div>
                {movement.entityType === 'strategy' && movement.playerName && (
                  <div className="welcome-eu-leaderboard-mission">{movement.playerName}</div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
      </div>
    </aside>
  );
}

export default WelcomeHonorHallMovements;
